import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import Sidebar from "../../components/Sidebar";
import { BillingSidebar } from "../../components/billing/BillingSidebar";
import { AccountingTabs } from "../../components/accounting/AccountingTabs";
import { formatINR } from "../../components/dashboard/formatINR";
import IncomeAPI, { IncomeEntry, IncomeSource } from "../../services/income.service";
import { IncomeEntryModal } from "../../components/accounting/IncomeEntryModal";

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export const IncomeListPage: React.FC = () => {
  const [entries, setEntries] = useState<IncomeEntry[]>([]);
  const [sources, setSources] = useState<IncomeSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<IncomeEntry | null>(null);
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(30));
  const [dateTo, setDateTo] = useState(todayIso());
  const [sourceFilter, setSourceFilter] = useState("");

  const sourceName = useCallback(
    (id: string) => sources.find(s => s.source_id === id)?.name ?? "—",
    [sources]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entryList, sourceList] = await Promise.all([
        IncomeAPI.list({ date_from: dateFrom, date_to: dateTo, source_id: sourceFilter || undefined }),
        IncomeAPI.listSources(),
      ]);
      setEntries(entryList);
      setSources(sourceList);
    } catch (err: any) {
      toast.error(err?.message || "Failed to load income entries");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (entry: IncomeEntry) => {
    if (!window.confirm(`Delete income entry of ${formatINR(entry.amount_paise)}?`)) return;
    try {
      await IncomeAPI.delete(entry.income_id);
      toast.success("Income entry deleted");
      load();
    } catch (err: any) {
      toast.error(err?.message || "Failed to delete income entry");
    }
  };

  const total = entries.reduce((sum, e) => sum + e.amount_paise, 0);

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 min-h-screen">
        <div className="max-w-[1300px] mx-auto px-4 py-6">
          <div className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[20px] shadow-[0px_10px_15px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col md:flex-row min-h-[calc(100vh-7rem)]">

            {/* Billing sub-navigation */}
            <BillingSidebar />

            {/* Main content */}
            <div className="flex-1 px-4 sm:px-8 py-6 sm:py-8 overflow-y-auto">
              <div className="flex flex-col gap-6 max-w-[920px]">

                <AccountingTabs />

                {/* ── Other Income ─────────────────────────────────────────── */}
                <section className="bg-white/60 border border-[rgba(206,250,254,0.5)] rounded-[14px] shadow-[0px_4px_6px_rgba(0,0,0,0.1)] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <h2 className="text-base font-semibold text-[#0f172b] flex items-center gap-2">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00b8db" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      Other Income
                    </h2>
                    <p className="text-xs text-slate-500">{entries.length} entries · Total {formatINR(total)}</p>
                  </div>
                  <p className="text-xs text-slate-400 -mt-3 mb-5">Consultation revenue is tracked separately under Billing.</p>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">From</label>
                      <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} max={dateTo}
                             className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">To</label>
                      <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} min={dateFrom}
                             className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#0a0a0a] mb-1">Source</label>
                      <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
                              className="w-full px-3 py-2 text-base sm:text-sm bg-white/50 border border-[#cefafe] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00b8db]/30">
                        <option value="">All sources</option>
                        {sources.map(s => <option key={s.source_id} value={s.source_id}>{s.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <button
                    onClick={() => { setEditingEntry(null); setModalOpen(true); }}
                    className="mb-5 py-2 px-4 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-[#00b8db] to-[#2b7fff] hover:opacity-90 transition shadow-sm"
                  >
                    + Record Income
                  </button>

                  <div className="rounded-[14px] border border-[#cefafe] bg-white/60 overflow-hidden">
                    {loading ? (
                      <div className="p-8 text-center text-slate-400 text-sm">Loading…</div>
                    ) : entries.length === 0 ? (
                      <div className="p-8 text-center text-slate-400 text-sm">No income entries in this range.</div>
                    ) : (
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[#cefafe] text-left text-slate-500">
                            <th className="px-4 py-3 font-medium">Date</th>
                            <th className="px-4 py-3 font-medium">Source</th>
                            <th className="px-4 py-3 font-medium">Counterparty</th>
                            <th className="px-4 py-3 font-medium">Payment</th>
                            <th className="px-4 py-3 font-medium text-right">Amount</th>
                            <th className="px-4 py-3 font-medium text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(e => (
                            <tr key={e.income_id} className="border-b border-[#e6fbfd] last:border-0 hover:bg-[#f0fdfe]">
                              <td className="px-4 py-3 text-slate-700">{e.income_date}</td>
                              <td className="px-4 py-3 text-slate-700">{sourceName(e.source_id)}</td>
                              <td className="px-4 py-3 text-slate-700">{e.counterparty || "—"}</td>
                              <td className="px-4 py-3 text-slate-500 capitalize">{e.payment_mode?.replace("_", " ") || "—"}</td>
                              <td className="px-4 py-3 text-right font-medium text-slate-900">{formatINR(e.amount_paise)}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => { setEditingEntry(e); setModalOpen(true); }}
                                  className="text-[#007595] hover:underline text-xs mr-3"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDelete(e)}
                                  className="text-red-500 hover:underline text-xs"
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </section>

              </div>
            </div>
          </div>
        </div>
      </div>

      <IncomeEntryModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={load}
        sources={sources}
        onSourceCreated={s => setSources(prev => [...prev, s])}
        editingEntry={editingEntry}
      />
    </div>
  );
};

export default IncomeListPage;
